import { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Button, Error } from '../../components';
import RoundBox from '../../components/RoundBox/RoundBox';
import { resursPay, ResursPayB2CPayload, ResursPayB2BPayload } from '../../api/resursPay';
import { IInvoice } from '../../interfaces';
import { colors } from '../../assets/colors';
import { Text, Line } from '../../helpers';
import ResursLogo from './ResursLogo';

type Props = {
    invoice: IInvoice;
    paymentMethodId: string;
};

const empty = (s: string) => !s || !s.trim();

const FieldWrapper = styled.div`
    margin-bottom: 2vh;
`;

const Label = styled.label`
    font-weight: 600;
    font-size: 14px;
    color: ${colors.dark};
    display: block;
    margin-bottom: 4px;
`;

const Input = styled.input`
    width: 100%;
    border: 1px solid #ecebea;
    border-radius: 5px;
    transition: border-color 0.2s ease;
    padding: 12px 20px;
    font-size: 16px;
    font-family: inherit;
    box-sizing: border-box;
    margin-top: 1vh;
    &:focus {
        border-color: ${colors.main};
        outline: none;
    }
`;

const Footer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 8px;
    font-size: 14px;
    color: ${colors.dark2};
`;

export default function ResursPayForm({ invoice, paymentMethodId }: Props) {
    const { t } = useTranslation();
    const isB2B = invoice.customer?.type === 'B2B';
    const c = invoice.customer ?? ({} as IInvoice['customer']);

    const [email, setEmail] = useState(c.email ?? '');
    const [name, setName] = useState(c.name ?? '');
    const [street_address, setStreetAddress] = useState(c.street_address ?? '');
    const [care_of, setCareOf] = useState(c.care_of ?? '');
    const [postal_code, setPostalCode] = useState(c.postal_code ?? '');
    const [city, setCity] = useState(c.city ?? '');
    const [id_number, setIdNumber] = useState(c.id_number ?? '');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const validate = (): boolean => {
        if (empty(email)) { setError(t('enter_email')); return false; }
        if (empty(name)) { setError(t('enter_name')); return false; }
        if (empty(street_address)) { setError(t('enter_address')); return false; }
        if (empty(postal_code)) { setError(t('enter_postal_code')); return false; }
        if (empty(city)) { setError(t('enter_city')); return false; }
        if (!isB2B && empty(id_number)) { setError(t('enter_number')); return false; }
        setError(null);
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (submitting || !validate()) return;
        setSubmitting(true);
        setError(null);
        try {
            const base = {
                payment_method: paymentMethodId,
                customer_email: email.trim(),
                name: name.trim(),
                street_address: street_address.trim(),
                care_of: (care_of ?? '').trim(),
                postal_code: postal_code.trim(),
                city: city.trim()
            };
            const payload: ResursPayB2CPayload | ResursPayB2BPayload = isB2B
                ? base
                : { ...base, id_number: id_number.trim() };
            const url = await resursPay(invoice.uuid, payload);
            window.location.href = url;
        } catch (err: unknown) {
            setSubmitting(false);
            let msg = t('somethingWentWrong');
            if (err && typeof err === 'object' && 'response' in err) {
                const res = (err as { response?: { status?: number; data?: unknown } }).response;
                if (res?.data && typeof res.data === 'object' && res.data !== null && 'detail' in res.data)
                    msg = String((res.data as { detail: unknown }).detail);
                else if (res?.status === 400)
                    msg = t('resursPayError');
            }
            setError(msg);
        }
    };

    return (
        <RoundBox>
            <Text i={t('invoiceInformation')} fontWeight="bold" header />
            {error && <Error message={error} />}
            <form onSubmit={handleSubmit}>
                <FieldWrapper>
                    <Label>{t('email')} *</Label>
                    <Input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('enter_email')}
                    />
                </FieldWrapper>
                {!isB2B && (
                    <FieldWrapper>
                        <Label>Personnummer *</Label>
                        <Input
                            type="tel"
                            value={id_number}
                            onChange={(e) => setIdNumber(e.target.value)}
                            placeholder="Använd tolv siffror"
                        />
                    </FieldWrapper>
                )}
                <FieldWrapper>
                    <Label>{t('name')} *</Label>
                    <Input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('enter_name')}
                    />
                </FieldWrapper>
                <FieldWrapper>
                    <Label>{t('street_address')} *</Label>
                    <Input
                        type="text"
                        value={street_address}
                        onChange={(e) => setStreetAddress(e.target.value)}
                        placeholder={t('enter_address')}
                    />
                </FieldWrapper>
                <FieldWrapper>
                    <Label>{t('care_of')}</Label>
                    <Input
                        type="text"
                        value={care_of}
                        onChange={(e) => setCareOf(e.target.value)}
                    />
                </FieldWrapper>
                <FieldWrapper>
                    <Label>{t('postal_code')} *</Label>
                    <Input
                        type="text"
                        value={postal_code}
                        onChange={(e) => setPostalCode(e.target.value)}
                        placeholder={t('enter_postal_code')}
                    />
                </FieldWrapper>
                <FieldWrapper>
                    <Label>{t('city')} *</Label>
                    <Input
                        type="text"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        placeholder={t('enter_city')}
                    />
                </FieldWrapper>
                <Button
                    type="submit"
                    i={t('resursPay')}
                    color={colors.white}
                    fontWeight="bold"
                    disabled={submitting}
                    style={{
                        width: '100%',
                        marginTop: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                />
            </form>
            <Line />
            <Footer>
                <span>Betallösningen hanteras av</span>
                <ResursLogo height="20" />
            </Footer>
        </RoundBox>
    );
}
